import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaGithub, FaLinkedin, FaYoutube, FaEnvelope } from 'react-icons/fa';
import vvcast from '../../assets/vvpodcast.png';

interface SocialLink {
  icon: typeof FaGithub;
  href: string;
  label: string;
  color: string;
}

const socialLinks: SocialLink[] = [
  {
    icon: FaGithub,
    href: "https://github.com/victorviccente",
    label: "GitHub",
    color: "hover:bg-gray-700"
  },
  {
    icon: FaLinkedin,
    href: "https://www.linkedin.com/in/victorviccente/",
    label: "LinkedIn",
    color: "hover:bg-blue-600"
  },
  {
    icon: FaYoutube,
    href: "https://www.youtube.com/@victorviccente",
    label: "YouTube",
    color: "hover:bg-red-600"
  }
];

const Hero: FC = () => {
  const [isEmailCopied, setIsEmailCopied] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText("seu@email.com");
    setIsEmailCopied(true);
    setTimeout(() => setIsEmailCopied(false), 2000);
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-between overflow-hidden bg-black">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-full h-full">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-blue-500/5"
              style={{
                width: Math.random() * 300 + 50,
                height: Math.random() * 300 + 50,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, Math.random() * 100 - 50],
                x: [0, Math.random() * 100 - 50],
                scale: [1, 1.1, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10 flex-grow flex flex-col justify-center items-center">
        <div className="max-w-5xl mx-auto text-center">
          {/* Profile Image */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8 relative group cursor-pointer"
          >
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt blur-lg"></div>
            <img
              src={vvcast}
              alt="Victor Vicente"
              className="w-40 h-40 rounded-full mx-auto object-cover transition-all duration-300 transform group-hover:scale-105 relative"
            />
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white tracking-tight">
              Victor Vicente
            </h1>

            {/* Social Links */}
            <motion.div 
              className="flex justify-center gap-4 mb-8"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {socialLinks.map((link, index) => {
                const Icon = link.icon;
                return (
                  <motion.a
                    key={link.label}
                    href={link.href}
                    
                    rel="noopener noreferrer"
                    className={`p-3 rounded-full bg-gray-900/50 backdrop-blur-sm transition-all duration-300 ${link.color} hover:scale-110`}
                    whileHover={{ y: -3 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.4 }}
                    aria-label={link.label}
                  >
                    <Icon className="w-6 h-6" />
                  </motion.a>
                );
              })}
              <motion.button
                onClick={copyEmail}
                className="p-3 rounded-full bg-gray-900/50 backdrop-blur-sm transition-all duration-300 hover:bg-green-600 hover:scale-110"
                whileHover={{ y: -3 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: socialLinks.length * 0.1 + 0.4 }}
                aria-label="Copy Email"
              >
                <FaEnvelope className="w-6 h-6 text-blue-300" />
              </motion.button>
            </motion.div>

            {/* Email copied notification */}
            <AnimatePresence>
              {isEmailCopied && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-blue-400 mb-4"
                >
                  Email copiado para a área de transferência!
                </motion.div>
              )}
            </AnimatePresence>

            {/* Call to Action Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <motion.button
                onClick={() => scrollToSection('#contact')}
                className="group relative px-6 py-3 rounded-lg text-base font-semibold overflow-hidden bg-blue-600 text-white"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="relative flex items-center justify-center">
                  <span>Entre em contato</span>
                  <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </motion.button>

              <motion.button
                onClick={() => scrollToSection('#projects')}
                className="px-6 py-3 rounded-lg text-base font-semibold bg-transparent border border-blue-500 text-blue-400 hover:bg-blue-900/30 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Ver projetos
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="w-full flex justify-center mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
      >
        <motion.div
          className="w-8 h-12 rounded-full border-2 border-blue-500 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <motion.div
            className="w-1 h-3 bg-blue-500 rounded-full mt-2"
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;